import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings2, X } from 'lucide-react';
import {
  DonateFeatureStatus,
  loadDonateFeatureStatus,
} from '../donate-kinds/donate-kinds';

/**
 * Nhắc creator (đang xem trang của chính mình) những kiểu donate chưa bật. Mọi tính năng mặc định
 * tắt, mà tắt thì trang không hiện gì cho người xem, nên creator dễ tưởng Cab không có. Mỗi mục
 * chưa bật dẫn thẳng tới đúng tab trong Cài đặt Donate (?tab=donate&muc=...).
 */

const HIDE_DAYS = 14;
const hideKey = (userId: string) => `donate-nudge-hidden:${userId}`;

const isHidden = (userId: string) => {
  try {
    const at = Number(localStorage.getItem(hideKey(userId)));
    return !!at && Date.now() - at < HIDE_DAYS * 24 * 3600 * 1000;
  } catch {
    return false;
  }
};

const settingsLink = (muc: string) => `/settings?tab=donate&muc=${muc}`;

const DonateSetupNudge: React.FC<{ userId: string }> = ({ userId }) => {
  const [items, setItems] = useState<DonateFeatureStatus[] | null>(null);
  const [hidden, setHidden] = useState(() => isHidden(userId));

  useEffect(() => {
    if (hidden) return undefined;
    let alive = true;
    loadDonateFeatureStatus(userId).then((r) => alive && setItems(r));
    return () => {
      alive = false;
    };
  }, [userId, hidden]);

  if (hidden || !items) return null;
  const on = items.filter((x) => x.state === 'on');
  const off = items.filter((x) => x.state === 'off');
  const needsObs = items.filter((x) => x.state === 'needs-obs');
  if (off.length === 0 && needsObs.length === 0) return null;

  const hide = () => {
    try {
      localStorage.setItem(hideKey(userId), `${Date.now()}`);
    } catch {
      // Không lưu được thì chỉ ẩn ở lần xem này.
    }
    setHidden(true);
  };

  return (
    <div className="relative rounded-2xl border border-[#DEE8FF] bg-[#F6F8FF] p-4 dark:border-slate-700 dark:bg-slate-800">
      <button
        type="button"
        onClick={hide}
        aria-label="Ẩn lời nhắc"
        className="absolute right-3 top-3 rounded-full p-1 text-[#627184] hover:bg-white dark:hover:bg-slate-700"
      >
        <X size={16} />
      </button>
      <p className="flex items-center gap-2 pr-8 text-base font-semibold text-[#121C2A] dark:text-white">
        <Settings2 size={18} className="text-blue-600" />
        Người xem đang donate cho bạn được những gì
      </p>
      <p className="mt-1 text-sm text-[#233040] dark:text-gray-300">
        Đang bật: {['Donate', ...on.map((x) => x.label)].join(', ')}.
      </p>
      {needsObs.map((x) => (
        <p key={x.key} className="mt-1 text-sm text-[#9A630C]">
          {x.label} đã bật nhưng lớp phủ OBS chưa mở, người xem chưa bấm được.
        </p>
      ))}
      {off.length > 0 && (
        <>
          <p className="mt-2 text-sm text-[#627184] dark:text-gray-400">
            Các tính năng dưới đây đang tắt hoặc chưa mở nên người xem không thấy. Bấm để cài đặt:
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {off.map((x) => (
              <Link
                key={x.key}
                to={settingsLink(x.muc)}
                className="rounded-full bg-[#EFF7FD] px-3 py-1.5 text-sm font-medium text-[#215C91] hover:bg-[#DCEEFB] dark:bg-slate-700 dark:text-blue-300"
              >
                {x.label}
              </Link>
            ))}
          </div>
        </>
      )}
      <p className="mt-3 text-xs text-[#627184] dark:text-gray-400">
        Muốn hiện bảng xếp hạng donate trên live? Lấy link ở{' '}
        <Link to={settingsLink('muc-tieu')} className="font-medium text-[#215C91] underline dark:text-blue-300">
          Cài đặt Donate
        </Link>
        .
      </p>
    </div>
  );
};

export default DonateSetupNudge;
